/**
 * Canonical appId aliases.
 *
 * A few apps are addressed in the browser URL / backend tile feed by a product
 * name that differs from the deployed MFE + role-screen name. They must resolve
 * to their canonical appId so the shell loads the right MFE and the role-screen
 * check (`validateScreenAccess` / legacy `AuthorizeService.validateRoute`)
 * matches the mapping.
 *
 * MAD ("Master Assets and Data") is the Next.js SUCCESSOR to the old Angular
 * `pie` Data app. It ships as its OWN deployed MFE (`/static/mad`); the legacy
 * shell loads it by URL convention via the `:app-id` catch-all, and the old
 * `pie` bundle, when its `isNextJsEnabled` flag is on, hands off to it with
 * `window.location.assign(.../app-container/mad?projectGuid=…)`.
 *
 * This alias exists ONLY for access control: `mad` has no entry of its own in
 * the role-screen mapping (which only lists `pie` / `workspace/apps/PIE`), so
 * `validateScreenAccess('mad', …)` would find nothing and bounce the user to
 * Home. Aliasing it to `pie` for the role check lets the Next.js app reuse the
 * `pie` mapping.
 *
 * CRITICAL: do NOT apply this alias to MFE LOADING. The `mad` URL must load the
 * real `/static/mad` bundle — aliasing it to `pie` there loads the OLD Angular
 * bundle, which immediately re-assigns to `mad` on boot → an infinite full-page
 * reload loop (see `ClientMfePage` in `router.tsx`).
 */
const APP_ID_ALIASES: Readonly<Record<string, string>> = {
  mad: 'pie',
};

/**
 * Resolve an appId to its canonical, lower-cased form, applying known aliases.
 * Non-aliased ids are returned lower-cased and otherwise unchanged.
 */
export function canonicalAppId(appId: string): string {
  const id = (appId ?? '').toLowerCase();
  return APP_ID_ALIASES[id] ?? id;
}
